import {
  DeleteButton,
  FloatingFormItem,
  Group,
  SaveButton,
} from '@/components/_common';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import {
  useBoschCoreConfigCreate,
  useBoschCoreConfigDetail,
  useBoschCoreConfigRemove,
  useBoschCoreConfigUpdate,
} from '@/core/domain/workorder/Bosch/Bosch.hooks';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { PageProps } from '@/types/PageProps';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Typography,
} from 'antd';
import { useEffect } from 'react';
import { CheckCircle, Edit, Plus, Save, XCircle } from 'react-feather';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

interface FormValues {
  username: string;
  password: string;
}

export default function CompanyBoschCoreCredentials({ route }: PageProps) {
  const intl = useIntl();
  const { companyId } = useCompanyFormContext();

  const requiredField = useDefaultEmptyFieldError();

  const [form] = Form.useForm<FormValues>();

  const boschConfigDetail = useBoschCoreConfigDetail(companyId);
  const boschConfigCreate = useBoschCoreConfigCreate();
  const boschConfigUpdate = useBoschCoreConfigUpdate();
  const boschConfigRemove = useBoschCoreConfigRemove();

  const hasSubmitError = !!boschConfigCreate.error || !!boschConfigUpdate.error;

  const hasDetail = !!boschConfigDetail.data?.id;
  const submiting = boschConfigCreate.isLoading;

  const [editable, { toggle: editableToggle }] = useDisclosure(false, {
    onClose: () => {
      if (!hasDetail) {
        form.setFields([
          { name: 'username', value: '', errors: [] },
          { name: 'password', value: '', errors: [] },
        ]);
      }

      boschConfigCreate.reset();
      boschConfigUpdate.reset();
    },
  });

  useEffect(() => {
    if (boschConfigDetail.data) {
      form.setFieldsValue({ username: boschConfigDetail.data.username });
    }
  }, [boschConfigDetail.data]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = { ...values, companyId };

      if (boschConfigDetail.data?.id) {
        await boschConfigUpdate.mutateAsync(payload);
      } else {
        await boschConfigCreate.mutateAsync(payload);
      }

      form.resetFields(['password']);

      editableToggle();
    } catch {}
  };

  const confirmRemove = () => {
    Modal.confirm({
      title: 'Confirmar remoção das credenciais',
      content:
        'Deseja remover as configuração de acesso ao Bosch CoRe? Ao remover perderá o acesso a geração do diagnostico via CoRe.',
      onOk: () =>
        boschConfigRemove.mutateAsync(companyId).then(() => {
          boschConfigDetail.remove();
          form.setFieldsValue({ username: '', password: '' });
        }),
      okText: 'Remover',
    });
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <div className="page">
        <div className="page-header" style={{ marginBottom: '24px' }}>
          <Typography.Title level={3}>Crendenciais Bosch CoRe</Typography.Title>
        </div>

        <p>
          Para habilitar a integração de geração de diagnóstico com o Bosch
          Connected Repair (CoRe), informe as credenciais de conexão.
        </p>

        <Divider />

        {hasSubmitError && (
          <Alert
            type="error"
            description={
              <div>
                <Group gap={16}>
                  <XCircle color="red" />
                  <div>
                    <span>Usuário ou senha estão inválidos</span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        opacity: 0.7,
                      }}
                    >
                      Verifique as credenciais no portal da oficina Bosch.
                    </span>
                  </div>
                </Group>
              </div>
            }
            style={{ marginBottom: 24 }}
          />
        )}

        {hasDetail && (
          <Alert
            type="success"
            description={
              <Group gap={16}>
                <CheckCircle color="green" />
                <span>Bosch CoRe habilitado para esta empresa</span>
              </Group>
            }
            style={{ marginBottom: 24 }}
          />
        )}

        <Form autoComplete="off" disabled={!editable || submiting} form={form}>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <FloatingFormItem
                    name="username"
                    label="Nome de usuário"
                    rules={[requiredField]}
                  >
                    <Input autoComplete="username" />
                  </FloatingFormItem>
                </Col>

                <Col span={24}>
                  <FloatingFormItem
                    name="password"
                    label="Senha de acesso"
                    rules={[requiredField]}
                  >
                    <Input.Password
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      {...({ autocomplete: 'new-password' } as any)}
                    />
                  </FloatingFormItem>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          {!editable && (
            <Col>
              <Button block onClick={editableToggle}>
                <Group>
                  {hasDetail && (
                    <>
                      <Edit size={14} />
                      <span>Editar</span>
                    </>
                  )}

                  {!hasDetail && (
                    <>
                      <Plus size={14} />
                      <span>Cadastrar</span>
                    </>
                  )}
                </Group>
              </Button>
            </Col>
          )}

          {editable && (
            <>
              <Col>
                <SaveButton onClick={handleSubmit} block loading={submiting}>
                  <Group>
                    <Save size={14} />
                    <span>Salvar</span>
                  </Group>
                </SaveButton>
              </Col>

              <Col>
                <Button onClick={editableToggle} block>
                  Cancelar
                </Button>
              </Col>
            </>
          )}

          {hasDetail && (
            <Col>
              <DeleteButton onClick={confirmRemove} block loading={submiting}>
                Remover
              </DeleteButton>
            </Col>
          )}
        </Row>
      </div>
    </>
  );
}
